import { useMemo } from 'react';
import { indexCategories } from '@/mocks/indexNav';
import { newsItems } from '@/mocks/newsFeed';

type NewsFeedProps = {
  query: string;
  activeCategory: string;
  onClearCategory: () => void;
  onClearQuery: () => void;
};

export default function NewsFeed({ query, activeCategory, onClearCategory, onClearQuery }: NewsFeedProps) {
  const activeName = indexCategories.find((item) => item.id === activeCategory)?.name ?? '';

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return newsItems.filter((item) => {
      const matchCategory = activeCategory ? item.category === activeCategory : true;
      const matchKeyword = keyword
        ? item.title.toLowerCase().includes(keyword) ||
          item.summary.toLowerCase().includes(keyword) ||
          item.tag.toLowerCase().includes(keyword)
        : true;
      return matchCategory && matchKeyword;
    });
  }, [query, activeCategory]);

  const hasFilter = Boolean(activeCategory || query.trim());

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center text-primary-600">
            <i className="ri-list-check-2" />
          </span>
          <span className="font-label text-sm tracking-wide text-foreground-900">短讯速览</span>
          <span className="font-label text-xs text-foreground-500">{filtered.length} 条</span>
        </div>
        {hasFilter ? (
          <button
            type="button"
            onClick={() => {
              onClearCategory();
              onClearQuery();
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 whitespace-nowrap cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-close-circle-line" />
            清除筛选
            {activeName ? <span className="text-foreground-500">· {activeName}</span> : null}
          </button>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-inbox-archive-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700">没有匹配的条目，换个关键词或清除筛选试试</p>
          <button
            type="button"
            onClick={() => {
              onClearCategory();
              onClearQuery();
            }}
            className="rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      ) : (
        <ol className="flex flex-col divide-y divide-background-200">
          {filtered.map((item, index) => (
            <li key={item.id} className="group py-4 first:pt-0">
              <article className="flex gap-4">
                <span className="font-label pt-0.5 text-lg leading-none text-background-400 transition-colors group-hover:text-primary-500 md:text-xl">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">{item.tag}</span>
                    <span className="text-[11px] text-foreground-500">{item.time}</span>
                  </div>
                  <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                </div>
                <div className="hidden shrink-0 flex-col items-end gap-1 pt-1 sm:flex">
                  <span className="flex items-center gap-1 font-label text-sm text-primary-600">
                    <i className="ri-fire-line text-xs" />
                    {item.heat}
                  </span>
                  <span className="text-[11px] text-foreground-500">热度值</span>
                </div>
              </article>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}